import cn from 'classnames'
import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { IFormJBPReportType } from 'legacy/lut/classes/models/partner360/IFormJBPReportType'
import { ExportCommitmentService } from 'legacy/lut/classes/services/partner360/ExportCommitmentService'
import {
  selectIsFilterApplied,
  selectPartnersInScope,
  selectPartnersType,
  selectProductsSelectValue,
  selectScopeSelectValue,
} from 'legacy/lut/features/partner360/composedReport'
import { IComposedFilterForReport } from 'legacy/lut/features/partner360/composedReport/models/state'
import { selectComposedReportFYear } from 'legacy/lut/features/partner360/composedReport/selectors/selectComposedReportFYear'
import { selectIsOpenScopeEditor } from 'legacy/lut/features/partner360/composedReport/selectors/selectIsOpenScopeEditor'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { Logic } from 'core-components/types'
import MeasureController from 'core/analytics/MeasureController'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'

import { exportSoBTrackerReport } from '../../api/exportSoBTrackerReport'

import { FilterButtons } from './filterButtons/FilterButtons'
import { FilterFields } from './filterFields/FilterFields'
import { useFilterActions } from './hooks/useFilterActions'
import { Scope } from './scope/Scope'
import { Warnings } from './warnings/Warnings'

import styles from './FilterArea.module.scss'

interface IProps {
  metrics: MeasureController
  onFilter: (filterForReport: IComposedFilterForReport, notFetchPartnersInScope: boolean) => void
  lastGuid: string | null
  setIsFiltersChanged: (isFilterChanged: boolean) => void
}

export const FilterArea = ({ metrics, onFilter, lastGuid, setIsFiltersChanged }: IProps) => {
  /** -------------------- State ----------------------- */

  const [isBusy, setIsBusy] = useState(false)
  const [reportTypes, setReportTypes] = useState<IFormJBPReportType[]>([])
  const [isLoading, setIsLoading] = useState(false)

  /** -------------------- Selectors ----------------------- */

  const isFilterApplied = useSelector(selectIsFilterApplied)
  const composedReportFYear = useSelector(selectComposedReportFYear)
  const scopeSelectValue = useSelector(selectScopeSelectValue)
  const partnersType = useSelector(selectPartnersType)
  const productsSelectValue = useSelector(selectProductsSelectValue)
  const partnersInScope = useSelector(selectPartnersInScope)
  const isOpenScopeEditor = useSelector(selectIsOpenScopeEditor)

  /** -------------------- Hooks ----------------------- */

  const { isP360Tester, isSuperAdmin } = useUserRoles()

  const {
    filter,
    cleanFilter,
    canClearFilter,
    canApplyFilter,
    isFilterWasChanged,
    showFirstFilterText,
    wip,
  } = useFilterActions({
    onFilter,
  })

  /** -------------------- Variables ----------------------- */

  const reportTypeCrud = CrudStore.get(
    CrudStoreItemPartner360.JBPReportType,
    'ComposedReportFilterArea'
  )

  const isBlureScope = useMemo(() => {
    return !showFirstFilterText && isFilterWasChanged && !isOpenScopeEditor
  }, [isFilterWasChanged, isOpenScopeEditor, showFirstFilterText])

  /** -------------------- Methods ----------------------- */

  const fetchReportData = useCallback(async () => {
    const result = await reportTypeCrud.fetch()
    setReportTypes(result.items || [])
  }, [reportTypeCrud])

  const onExportSoBTrackerClick = useCallback(async () => {
    setIsLoading(true)
    const reportType = reportTypes.find((el) => el.Id === 'jbp-composed-report')
    if (reportType) {
      setIsBusy(true)
      await ExportCommitmentService.generateJBPReportNew({
        reportType,
        guid: lastGuid ?? undefined,
        fyear: composedReportFYear,
        scope: scopeSelectValue,
        partnerType: partnersType,
        products: productsSelectValue,
        globalPartnerName: partnersInScope,
      })
      setIsBusy(false)
    } else {
      Logger.error('JBPReportType has no jbp-composed-report item')
    }
    setIsLoading(false)
  }, [
    reportTypes,
    lastGuid,
    composedReportFYear,
    partnersInScope,
    partnersType,
    scopeSelectValue,
    productsSelectValue,
  ])

  const onExportSoBTrackerClickOld = useCallback(async () => {
    setIsBusy(true)
    await exportSoBTrackerReport(lastGuid, composedReportFYear)
    setIsBusy(false)
  }, [lastGuid, composedReportFYear])

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    setReportTypes([])
    reportTypeCrud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'Id',
          operator: 'eq',
          value: 'jbp-composed-report',
        },
      ],
    })
    fetchReportData()
  }, [fetchReportData, reportTypeCrud])

  useEffect(() => {
    setIsFiltersChanged(!showFirstFilterText && isFilterWasChanged)
  }, [showFirstFilterText, isFilterWasChanged, setIsFiltersChanged])

  /** -------------------- Result ----------------------- */

  return (
    <div className={styles.filterArea}>
      <FilterFields />
      <div className={styles.filterActions}>
        <div className={styles.btnContainer}>
          <Button
            className={styles.btn}
            disabled={isBusy || !isFilterApplied}
            onClick={onExportSoBTrackerClick}
          >
            <Translation text="jbp-sob-tracker-report-export" />
          </Button>
          {(isP360Tester || isSuperAdmin) && (
            <Button
              className={styles.btn}
              disabled={isBusy || !isFilterApplied}
              onClick={onExportSoBTrackerClickOld}
            >
              <Translation text="jbp-sob-tracker-report-export-old" />
            </Button>
          )}
          {isLoading && (
            <div className={styles.busyIndicator}>
              <Loader />
            </div>
          )}
        </div>
        <FilterButtons
          canClearFilter={canClearFilter}
          filter={filter}
          cleanFilter={cleanFilter}
          metrics={metrics}
          canApplyFilter={canApplyFilter}
        />
      </div>
      <Warnings
        wip={wip}
        showFirstFilterText={showFirstFilterText}
        isFilterWasChanged={isFilterWasChanged}
      />
      <div className={cn({ [styles.blurred]: isBlureScope })}>
        <Scope metrics={metrics} />
      </div>
    </div>
  )
}
